// Route, Vehicle, and Driver filter configurations

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const routeFilters: FilterRegistry = {
	// Despite the legacy `route*` filter ids and the `objectTable: 'route'`
	// SQL alias, every entry below is consumed only by the *Trips* list
	// (`TRIP_LIST_CONFIG.filters.activeFilters`). Post-Phase-A the
	// per-day instance table IS `route` (trip data) and the recurring
	// blueprint list registers no filters. So the user-facing labels here
	// are trip-flavored. The filter ids stay as `route*` because changing
	// them would invalidate every saved filter the user has stored on
	// the Trips list — a no-op-for-the-user rename that breaks state.
	//
	// PD-13732: each filter carries `listKeys: ['route', 'trip']`. The active
	// consumer is the Trips list, which renders under `listKey="trip"`
	// (TripsList.tsx). `FilterService.transformFiltersForBackend` drops any
	// filter whose `listKeys` doesn't include the active list's key, so the
	// old `['route']`-only tag silently stripped EVERY trip filter from the
	// outbound request — the backend then received `filters={}` and every
	// status tab showed the same unfiltered list. `'trip'` makes them
	// serialize; `'route'` is retained for the blueprint list (route.config.ts),
	// which shares the same `route*` ids and `objectTable: 'route'` and may
	// register these filters later.
	routeSearch: {
		...createKeywordFilter({
			// Searches the names on each trip row — route (template) name, driver
			// name, and vehicle name — so "Name" reads truer than "Search".
			label: 'Name',
			placeholder: 'Search by name...',
			objectTable: 'route',
			listKeys: ['route', 'trip'],
		}),
		queryParamName: 'routeSearch',
	},

	routeDateRange: {
		...createDateRangeFilter({
			label: 'Trip Date',
			objectTable: 'route',
			dateField: 'date',
			listKeys: ['route', 'trip'],
		}),
		queryParamName: 'routeDateRange',
	},

	routeDriver: {
		...createEntityLookupFilter({
			label: 'Driver',
			placeholder: 'Select driver...',
			apiEndpoint: '/users',
			objectTable: 'route',
			fieldName: 'driverId',
			listKeys: ['route', 'trip'],
			displayFields: {
				id: 'id',
				primary: 'fullName',
			},
		}),
		queryParamName: 'routeDriver',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	routeVehicle: {
		...createEntityLookupFilter({
			label: 'Vehicle',
			placeholder: 'Select vehicle...',
			apiEndpoint: '/vehicles',
			objectTable: 'route',
			fieldName: 'vehicleId',
			listKeys: ['route', 'trip'],
			displayFields: {
				id: 'id',
				primary: 'name',
			},
		}),
		queryParamName: 'routeVehicle',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	routeStatus: {
		...createStatusFilter({
			label: 'Status',
			objectTable: 'route',
			fieldName: 'status',
			listKeys: ['route', 'trip'],
			options: [
				// `pending_load` was added 2026-05-12 — sits between
				// dispatch-commit and pre-trip. Warehouse manager owns it.
				{ id: 'pending_load', label: 'Pending Load' },
				{ id: 'scheduled', label: 'Scheduled' },
				{ id: 'in-progress', label: 'In Progress' },
				{ id: 'completed', label: 'Completed' },
				{ id: 'failed', label: 'Failed' },
				{ id: 'cancelled', label: 'Cancelled' },
			],
		}),
		queryParamName: 'routeStatus',
	},

	// PD-13742: Warehouse filter for the Trips / Routes lists. Options come from
	// /warehouses (already scoped to the caller's accessible warehouses). The
	// value is kept as a STRING (no number transform) so the `MY_WAREHOUSE`
	// default token survives to the backend, which resolves it to the user's
	// active warehouse; an explicitly-picked warehouse arrives as its id string.
	tripWarehouse: {
		...createEntityLookupFilter({
			label: 'Warehouse',
			placeholder: 'Select warehouse...',
			apiEndpoint: '/warehouses',
			objectTable: 'route',
			fieldName: 'warehouseId',
			listKeys: ['route', 'trip'],
			displayFields: {
				id: 'id',
				primary: 'name',
			},
		}),
		queryParamName: 'tripWarehouse',
		valueExtractor: {
			property: 'id',
		},
	},

	// Vehicle filters
	vehicleSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search vehicles...',
			objectTable: 'vehicle',
			listKeys: ['vehicle'],
		}),
		queryParamName: 'vehicleSearch',
	},

	vehicleStatus: {
		...createStatusFilter({
			label: 'Status',
			objectTable: 'vehicle',
			fieldName: 'status',
			listKeys: ['vehicle'],
			options: [
				{ id: 'active', label: 'Active' },
				{ id: 'inactive', label: 'Inactive' },
				{ id: 'maintenance', label: 'Maintenance' },
				{ id: 'offline', label: 'Offline' },
			],
		}),
		queryParamName: 'vehicleStatus',
	},

	vehicleType: {
		...createKeywordFilter({
			label: 'Type',
			placeholder: 'Filter by type...',
			objectTable: 'vehicle',
			listKeys: ['vehicle'],
		}),
		queryParamName: 'vehicleType',
	},

	// Trip Status is a DERIVED status (a vehicle is "On a Trip" when it has an
	// active trip — in-progress / pending-driver-confirm — otherwise it's
	// "Available"). It is NOT a column, so there is intentionally no
	// `queryMapping`: the vehicles backend reads the `vehicleTripStatus` query
	// param and applies the trip EXISTS subquery itself. Single-select because
	// the two states are mutually exclusive. This filter also backs the
	// "On a Trip" / "Available" tabs via their filterRules.
	vehicleTripStatus: {
		type: 'single-select',
		label: 'Trip Status',
		placeholder: 'Select trip status...',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: 'on-trip', label: 'On a Trip' },
			{ id: 'available', label: 'Available' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'vehicleTripStatus',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['vehicle'],
	},

	// Driver filters
	driverSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search drivers...',
			objectTable: 'driver',
			listKeys: ['driver'],
		}),
		queryParamName: 'driverSearch',
	},

	// Trip Status is a DERIVED status (a driver is "On a Trip" when they have an
	// active trip — in-progress / pending-driver-confirm — otherwise they're
	// "Available"). It is NOT a column, so there is intentionally no
	// `queryMapping`: the drivers backend reads the `driverTripStatus` query
	// param and applies the trip EXISTS subquery (via `trip.driverUserId`)
	// itself. Single-select because the two states are mutually exclusive. This
	// filter also backs the "On a Trip" / "Available" tabs via their filterRules.
	// It replaces the retired active/inactive `driverStatus` filter, which
	// referenced `u.isActive` in a HAVING clause and threw
	// "Unknown column 'u.isActive' in 'having clause'".
	driverTripStatus: {
		type: 'single-select',
		label: 'Trip Status',
		placeholder: 'Select trip status...',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: 'on-trip', label: 'On a Trip' },
			{ id: 'available', label: 'Available' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'driverTripStatus',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['driver'],
	},
};
